import { useEffect, useRef } from 'react'

import { CancelledError } from '@tanstack/react-query'
import { createRouter, redirect, RouterProvider } from '@tanstack/react-router'

import { env } from '@/env'
import { routeTree } from '@/routeTree.gen'
import { useAuth, useClerk, useUser } from '@clerk/react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'

import { queryClient } from '@/lib/react-query'

import { useAppLoader } from '@/features/app-shell/app-loader-context'

import { isAbortError, isApiError } from '@/shared/api/utils/error'

import {
	API_URL,
	appVersion,
	isDEV,
	isMockingEnabled,
	isPRODDeploy,
} from './lib/constants'

import type { ApiError } from '@/shared/api/utils/error'

/** Max time to wait for Clerk to load before forcing logout */
const CLERK_LOAD_TIMEOUT_MS = 10_000

// Create a new router instance
const router = createRouter({
	routeTree,
	// Pass the query client to the router context
	context: {
		queryClient,
		clerk: {
			user: null,
			auth: null,
			client: null,
		},
	},
	defaultPreload: 'intent',
	// Restore scroll position on back/forward (POP) navigations; the cached
	// position wins over hash-scroll, so forward deep-links scroll to the hash
	// target while the back button restores where you were. Inner scroll
	// containers opt in via `data-scroll-restoration-id` (see AIRailLayout).
	scrollRestoration: true,
	// Since we're using React Query, we don't want loader calls to ever be stale
	// This will ensure that the loader is always called when the route is preloaded or visited
	defaultPreloadStaleTime: 0,
	defaultPendingMs: 300,
	defaultStructuralSharing: true,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}

/**
 * Sentry Initialization
 * This will initialize Sentry with the DSN and Tanstack Router integration.
 * Skipped entirely when mocking (ADR 0006 decision 6): the DSN points at
 * production and a dummy DSN still triggers SDK bootstrap traffic — skip
 * beats mis-configure.
 */
if (!isDEV && !isMockingEnabled) {
	Sentry.init({
		dsn: env.VITE_SENTRY_DSN,
		integrations: [
			// Router Tracing
			Sentry.tanstackRouterBrowserTracingIntegration(router),
			Sentry.eventFiltersIntegration(),
		],

		environment: env.VITE_TARGET_STAGE,
		release: `air-dashboard@${appVersion}`,

		// Set trace targets for your API
		tracePropagationTargets: ['localhost', API_URL],

		tracesSampleRate: isPRODDeploy ? 0.2 : 0.75,
		profilesSampleRate: isPRODDeploy ? 0.2 : 0.75,

		ignoreErrors: [
			// Network errors we don't control
			'NetworkError',
			'Failed to fetch',

			// Browser quirks
			'Non-Error promise rejection captured',
			'ResizeObserver loop limit exceeded',

			// Extension conflicts
			'chrome-extension://',
			'moz-extension://',

			// ChunkLoadError already handled
			'ChunkLoadError',

			// Clerk dev keys warning
			'Clerk has been loaded with development keys',
		],

		// Critical error filtering
		beforeSend(event, hint) {
			const error = hint.originalException as ApiError | Error

			// Filter AbortErrors with comprehensive checking
			if (isAbortError(error) || error instanceof CancelledError) {
				if (isDEV) {
					console.debug('[Sentry] Filtered abort error in beforeSend:', error)
				}
				return null
			}

			// Additional abort check for edge cases
			if (error instanceof Error) {
				const message = error.message.toLowerCase() || ''
				if (
					message.includes('signal is aborted') ||
					message.includes('aborted') ||
					message.includes('user aborted') ||
					message.includes('operation was aborted')
				) {
					if (isDEV) {
						console.debug(
							'[Sentry] Filtered abort-like error in beforeSend:',
							error,
						)
					}
					return null
				}
			}

			// API errors caught by error boundaries should be reported with context
			// All other API errors are filtered — backend handles them
			if (isApiError(error)) {
				if (event.tags?.caughtByBoundary === 'true') {
					event.fingerprint = [
						'boundary-api-error',
						String(event.tags?.feature ?? 'unknown'),
						String(error.status),
					]
					return event
				}

				// The second way through: a call site that asked for this event by
				// name (`reportApiError` in `lib/sentry/ui-error-reporter.ts`). A
				// failed WRITE that only toasts reaches no boundary, so the rule above
				// would drop it and the team would see none of it — the toast is the
				// user's answer, not ours. The fingerprint is left as the reporter set
				// it (`ui-error` / source / feature / error name): it already groups by
				// the failing action, which is finer than status would be.
				if (event.tags?.reportApiError === 'true') return event

				if (isDEV) {
					console.debug(
						`[Sentry] Filtered API error ${error.status} in beforeSend:`,
						error,
					)
				}
				return null
			}

			return event
		},
	})
}

function App() {
	// Clerk Auth
	const auth = useAuth()
	const user = useUser()
	const client = useClerk()

	const isClerkLoaded = user.isLoaded && client.loaded && auth.isLoaded
	const hasTimedOutRef = useRef(false)

	const { showLoader, hideLoader } = useAppLoader()

	useEffect(() => {
		if (!isClerkLoaded) {
			showLoader()
		} else {
			hideLoader()
		}
	}, [isClerkLoaded, showLoader, hideLoader])

	// Timeout: if Clerk doesn't load within CLERK_LOAD_TIMEOUT_MS, force redirect to login
	useEffect(() => {
		if (isClerkLoaded || hasTimedOutRef.current) return

		const timeoutId = setTimeout(() => {
			if (!isClerkLoaded) {
				hasTimedOutRef.current = true

				Sentry.addBreadcrumb({
					category: 'auth',
					message: 'Clerk load timeout - forcing redirect to login',
					level: 'warning',
					data: {
						userLoaded: user.isLoaded,
						clientLoaded: client.loaded,
						authLoaded: auth.isLoaded,
					},
				})

				toast.error('Session could not be verified. Please log in again.')

				// Force redirect to login page
				throw redirect({ to: '/logout', replace: true })
			}
		}, CLERK_LOAD_TIMEOUT_MS)

		return () => clearTimeout(timeoutId)
	}, [isClerkLoaded, user.isLoaded, client.loaded, auth.isLoaded])

	// !IMPORTANT: We can't render anything if Clerk isn't loaded, this prevents race conditions where Clerk isn't loaded and a route tries to validate the user
	if (!isClerkLoaded) {
		return null
	}

	// Tier 1: early Sentry user context for auth route errors and failed session seeds
	// Tier 2 enrichment (role, SKU) happens in seedDashboardSession after /me fetch
	if (auth.isSignedIn) {
		Sentry.setUser({
			id: auth.userId ?? undefined,
			email: user.user?.primaryEmailAddress?.emailAddress,
		})
	}

	return (
		<RouterProvider
			router={router}
			context={{
				queryClient,
				clerk: {
					user,
					auth,
					client,
				},
			}}
		/>
	)
}

export default App
